import { Card, InfoTip, cn } from "@app/ui";
import { useEffect, useState } from "react";

import {
  CalendarsPanel,
  LabelsPanel,
  OrganizeTabs,
  TopicsPanel,
  type OrganizeTab,
} from "../../features/organize/index.js";
import { useI18n } from "../../lib/useI18n.js";
import { useMobileUx } from "../../lib/useMobileUx.js";
import {
  useCalendarsStore,
  useLabelsStore,
  usePaletteBoardStore,
  useTopicsStore,
  type BoardTargetRef,
} from "../../stores/index.js";
import { SettingsAnchor } from "./SettingsCard.js";
import { PaletteBoardWithPreview } from "./theme/PaletteBoardWithPreview.js";
import { PresetPickerCard } from "./theme/PresetPickerCard.js";
import { useSettingsFocusStore } from "./useSettingsFocus.js";

const TAB_IDS: OrganizeTab[] = ["topics", "calendars", "labels"];

/** "Topics, Labels and Calendars" tab — ONE home for topics, calendars and
 *  labels: create, rename, color (via the palette board, which must always
 *  travel with the preset chooser, plus the live calendar preview at its side
 *  on desktop, matching the UI Colors tab), reorder, nest, per-view
 *  visibility, delete. The board is sticky on desktop so an armed row's paint
 *  surface stays visible while scrolling;
 *  mobile scrolls the board back into view when a color control is armed. */
export function OrganizeSettings() {
  const { t } = useI18n();
  const mobile = useMobileUx();
  const [tab, setTab] = useState<OrganizeTab>("topics");
  const focusId = useSettingsFocusStore((s) => s.focusId);

  // Settings-search results target the panel anchors — switch to their tab.
  useEffect(() => {
    if (focusId && (TAB_IDS as string[]).includes(focusId)) setTab(focusId as OrganizeTab);
  }, [focusId]);

  // Keep the armed board target within the visible tab: arriving with (or
  // switching to) a tab whose entities can't be armed leaves the board painting
  // an invisible row, so re-arm the tab's first entity. Subscribing to the
  // first ids re-fires the effect once the stores hydrate after mount.
  const firstTopicId = useTopicsStore((s) => s.topics[0]?.id ?? null);
  const firstCalendarId = useCalendarsStore((s) => s.calendars[0]?.id ?? null);
  const firstLabelId = useLabelsStore((s) => s.labels[0]?.id ?? null);
  const armedKind = usePaletteBoardStore((s) => {
    const target = s.target;
    if (!target) return null;
    if (target.kind === "label") return "labels";
    if (target.kind === "entity" && target.target.kind === "topic") return "topics";
    if (target.kind === "entity" && target.target.kind === "calendar") return "calendars";
    return null;
  });
  const defaultTarget = defaultTargetFor(tab, firstTopicId, firstCalendarId, firstLabelId);
  useEffect(() => {
    if (armedKind === tab || !defaultTarget) return;
    usePaletteBoardStore.getState().arm(defaultTarget);
  }, [tab, armedKind, firstTopicId, firstCalendarId, firstLabelId]);

  return (
    <div className="flex flex-col gap-6">
      <PresetPickerCard anchorId="organize-preset" />

      {/* The sticky wrapper must remain a DIRECT child of the page column —
          nesting it deeper would end its sticking early (see ThemeSettings). */}
      <div className={cn(!mobile && "sticky top-0 z-10 bg-background pb-2")}>
        <PaletteBoardWithPreview
          boardAnchorId="organize-board"
          previewAnchorId="organize-preview"
          defaultTarget={defaultTarget ?? undefined}
        />
      </div>

      <div className="flex flex-col gap-4">
        <OrganizeTabs tab={tab} onChange={setTab} />
        <SettingsAnchor id={tab}>
          <Card className="p-4">
            <h2 className="mb-2 flex items-center gap-1.5 font-medium">
              {t(tab === "topics" ? "common.topics" : tab === "calendars" ? "common.calendars" : "common.labels")}
              <InfoTip label={t("common.moreInfo")}>
                {t(tab === "topics" ? "lists.topics.description" : tab === "calendars" ? "lists.calendars.description" : "lists.labels.description")}
              </InfoTip>
            </h2>
            {tab === "topics" && <TopicsPanel />}
            {tab === "calendars" && <CalendarsPanel />}
            {tab === "labels" && <LabelsPanel />}
          </Card>
        </SettingsAnchor>
      </div>
    </div>
  );
}

/** The first entity of the active tab — what the board paints before any row
 *  was armed explicitly. */
function defaultTargetFor(
  tab: OrganizeTab,
  firstTopicId: string | null,
  firstCalendarId: string | null,
  firstLabelId: string | null,
): BoardTargetRef | null {
  if (tab === "topics") return firstTopicId ? { kind: "entity", target: { kind: "topic", id: firstTopicId } } : null;
  if (tab === "calendars") return firstCalendarId ? { kind: "entity", target: { kind: "calendar", id: firstCalendarId } } : null;
  return firstLabelId ? { kind: "label", id: firstLabelId } : null;
}
